import {
  appAuthAtom,
  AppAuthState,
  AuthStage,
  AuthState,
  ConnectionState,
  debugObjectsAtom,
  eStreamConnectionStateAtom,
  SSESource,
  UnknownAppStage,
} from '@eih-netbroker/common';
import { PrimitiveAtom } from 'jotai/vanilla/atom';
import { store } from '../../state/store';
import { createLogger } from '../../utils/logger';
import { SSECommunicatorWeb } from './SSECommunicatorWeb';

/**
 * SSE class is responsible for managing the SSE connection through the SSECommunicator
 *
 * Initiate the SSE connections on auth state changes
 *  - authenticated > initiate the UserStream connections after loading the user settings
 *  - signoff > disconnect the SSE connections
 */
class SSEC {
  private readonly logger = createLogger(`SSE`);

  loginInitiatedFromOtherInstance: boolean = false;
  private _sseCommunicator?: SSECommunicatorWeb;
  private userStreamInitInProgress: boolean = false;
  private eStreamInitStarted: boolean = false;
  private disconnectInProgress: boolean = false;

  constructor() {
    store.sub(appAuthAtom, () => {
      this.handleUnauthenticatedState();
      this.handleAuthenticatedState();
    });
  }

  get authState() {
    return store.get(appAuthAtom);
  }

  set authState(val: AppAuthState) {
    store.set(appAuthAtom as PrimitiveAtom<AppAuthState>, val);
  }

  set eStreamConnectionState(val: ConnectionState | undefined) {
    store.set(eStreamConnectionStateAtom as PrimitiveAtom<ConnectionState | undefined>, val);
  }

  set debugObjects(val: any) {
    store.set(debugObjectsAtom, val);
  }

  get sseCommunicator() {
    if (!this._sseCommunicator) {
      this._sseCommunicator = SSECommunicatorWeb.create({
        offlineMode: false,
        createLogger,
      });
    }
    return this._sseCommunicator;
  }

  private handleUnauthenticatedState() {
    if (
      !this.authState.isAuthenticated &&
      this.authState.authStatus === AuthState.Unknown &&
      this.authState.stage === UnknownAppStage.InitSSEManager
    ) {
      // if the auth status is Unknown, and we are in the stage of initializing the SSE Manager, we need to initialize the SSE Manager,
      // so we can find out if there is a session in another tab, or we can just redirect the user to the login page
      this.sseCommunicator
        .initSSEManager()
        .then((shouldLoadLogin: boolean | null) => {
          if (shouldLoadLogin) {
            this.logger.info(`No session found in another tab, redirecting to login`);
            // perfectly fine, we can redirect the user to the login page
            this.authState = { isAuthenticated: false, authStatus: AuthState.NotAuthenticated };
          }
        })
        .catch(err => {
          this.logger.error(`Failed to initialize SSE Manager`, err);
          // something went wrong, we need to redirect the user to the login page
          this.authState = { isAuthenticated: false, authStatus: AuthState.SignOff };
        });
    } else if (!this.authState.isAuthenticated && this.authState.authStatus === AuthState.NotAuthenticated) {
      // if the auth status is NotAuthenticated we need to reset the login initiated from broadcast flag
      this.loginInitiatedFromOtherInstance = false;
    }
  }

  private handleAuthenticatedState() {
    if (this.authState.isAuthenticated) {
      this.handleLoadUserSettings();
      this.handleEStreamConnect();
    }
    this.handleSignOff();
  }

  /**
   * When user gets authenticated, and we are loading user settings, we need to broadcast the login started event
   * @private
   */
  private handleLoadUserSettings() {
    if (
      this.authState.isAuthenticated &&
      this.authState.authStatus === AuthState.Loading &&
      this.authState.stage === AuthStage.Step1_LoadUserSettings &&
      !this.loginInitiatedFromOtherInstance
    ) {
      // if we are authenticated and loading user settings we need to:
      //  - broadcast the login started event
      //  - take over as the primary instance in SSEManager
      //  - initiate the UserStream connection
      if (!this.userStreamInitInProgress) {
        // do not allow multiple calls, unfortunately states are changing multiple times, so this useEffect calls frequently
        this.userStreamInitInProgress = true;

        this.sseCommunicator.takeOverPrimaryInstance();
        this.sseCommunicator.connectSSEClients(this.authState.secrets.JwtToken, SSESource.UserStream);
      }
    }
  }

  private handleEStreamConnect() {
    if (this.authState.authStatus === AuthState.Authenticated && this.authState.secrets) {
      // if we are authenticated we need to broadcast the login completed event and initiate the eStream connection

      if (!this.eStreamInitStarted && !this.loginInitiatedFromOtherInstance) {
        this.eStreamInitStarted = true;
        this.sseCommunicator.connectSSEClients(this.authState.secrets.JwtToken, SSESource.EStream);
      }
    }
  }

  private handleSignOff() {
    if (this.authState.authStatus === AuthState.SignOff) {
      if (!this.disconnectInProgress) {
        this.disconnectInProgress = true;
        this.disconnectSSE(true);
        this.userStreamInitInProgress = false;
        this.eStreamInitStarted = false;
      }
    }
    if (this.authState.authStatus === AuthState.NotAuthenticated) {
      this.disconnectInProgress = false;

      // double check and set the states to false, because we could skip the SignOff state
      this.eStreamInitStarted = false;
      this.userStreamInitInProgress = false;
    }
  }

  private disconnectSSE(force: boolean) {
    this.sseCommunicator.disconnect(force);

    this.eStreamConnectionState = undefined;
    this.debugObjects = { EStreamConnectionState: undefined };
  }
}

export const SSE = new SSEC();
